import React from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { ArrowRight, Code2, Cpu, Database, Wrench, Server, FileText, Search } from 'lucide-react';
import { techStack } from '../data/mock';
import { ParticlesBg } from '../components/layout/ParticlesBg';
import { Section, SectionHeading, PageHero, Ridgeline, fadeInUp } from '../components/layout/Section';
import { SpotlightCard, LogoTile, Marquee, Reveal, CountUp } from '../components/layout/Tech';
import { TechCube } from '../components/layout/Motion3D';
import { ChartCard, BarsH } from '../components/layout/Charts';

const categoryIcons = { 'Frontend': Code2, 'Backend': Server, 'Data / Infra': Database, 'Content / SEO': Search, 'AI / RAG': Cpu, 'Document / OCR': FileText };
const categoryAccents = { 'Frontend': 'accent-purple', 'Backend': 'accent-blue', 'Data / Infra': 'accent-green', 'Content / SEO': 'accent-cyan', 'AI / RAG': 'accent-orange', 'Document / OCR': 'accent-pink' };

const mainStack = ['React', 'TypeScript', 'Vite', 'Astro', 'FastAPI', 'Supabase', 'PostgreSQL', 'Redis', 'Cloudflare', 'Gemini', 'OpenAI', 'Pydantic AI', 'Kreuzberg'];

const allTools = [...techStack.frontend, ...techStack.backend, ...techStack.dataInfra, ...techStack.contentSeo, ...techStack.aiRag, ...techStack.documentOcr];
const cubeItems = allTools.filter((t) => t.logo).filter((t, i, arr) => arr.findIndex((x) => x.logo === t.logo) === i).slice(0, 24);
const logoFor = (name) => (allTools.find((t) => t.name.toLowerCase().startsWith(name.toLowerCase())) || {}).logo;

const branchGroups = [
  { title: 'Frontend Experience', category: 'Frontend', summary: 'Application UI, static sites, maps, 3D, charts, forms, animation, and multilingual interfaces.', items: techStack.frontend },
  { title: 'Backend & Serverless', category: 'Backend', summary: 'Python APIs, backend platform services, authentication, and edge/serverless workflows.', items: techStack.backend },
  { title: 'Data & Infrastructure', category: 'Data / Infra', summary: 'Databases, queues, containers, deployment, observability, package management, and source control.', items: techStack.dataInfra },
  { title: 'Content & SEO', category: 'Content / SEO', summary: 'Static generation, search, CMS content, structured data, sitemap, and metadata tooling.', items: techStack.contentSeo },
  { title: 'AI & RAG', category: 'AI / RAG', summary: 'Chat, embeddings, typed agents, retrieval, reranking, and context compression.', items: techStack.aiRag },
  { title: 'Document / OCR', category: 'Document / OCR', summary: 'Focused extraction and OCR tooling.', items: techStack.documentOcr }
];

const overview = [
  { title: 'React / TypeScript / Vite / Astro Frontend', desc: 'Frontend work uses React 18, TypeScript, Vite, Astro 6, Astro components, CSS, Astro Assets, react-router-dom, Radix UI, Tailwind CSS, Zustand, TanStack React Query, Recharts, forms, validation, animation, maps, 3D, export, QR/barcode, toast, and i18n tooling.', accent: 'accent-purple', icon: Code2 },
  { title: 'FastAPI / Supabase Backend', desc: 'Backend work uses FastAPI, Uvicorn, Supabase, Supabase Auth, and Supabase Edge Functions for API and serverless workflows.', accent: 'accent-blue', icon: Server },
  { title: 'PostgreSQL / Redis / Cloud Deployment', desc: 'Data and infrastructure use PostgreSQL with SQLAlchemy, asyncpg, psycopg2, Redis queues and cache, Docker, Railway, Cloudflare Pages, Cloudflare Workers, Wrangler, Node.js, npm, GitHub, OpenTelemetry, and structlog.', accent: 'accent-green', icon: Database },
  { title: 'WordPress / Pagefind / SEO', desc: 'Content and SEO workflows use WordPress REST API, WordPress CMS, Pagefind search, JSON-LD schema, @astrojs/sitemap, and meta tags.', accent: 'accent-cyan', icon: Search },
  { title: 'Gemini / OpenAI / Pydantic AI RAG', desc: 'The AI stack uses Gemini via google-genai for chat, OpenAI embeddings, Pydantic AI agents, pgvector-style hybrid retrieval, FlashRank reranking, and LLMLingua compression.', accent: 'accent-orange', icon: Cpu },
  { title: 'Kreuzberg Document / OCR Processing', desc: 'Kreuzberg powers document and OCR extraction in the application stack.', accent: 'accent-pink', icon: FileText }
];

const BranchCard = ({ branch, index }) => {
  const Icon = categoryIcons[branch.category] || Wrench;
  const accent = categoryAccents[branch.category] || 'accent-grey';
  return (
    <Reveal delay={index * 0.06} className="h-full">
      <SpotlightCard accent={accent} className="h-full" style={{ '--card-pad': '1.75rem' }} as="article">
        <div className="flex items-start gap-4 mb-5">
          <div className="icon-well"><Icon className="w-6 h-6" /></div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3">
              <p className="kicker mb-1.5">{branch.category}</p>
              <span className="chip" style={{ fontSize: '0.64rem' }}>{branch.items.length} tools</span>
            </div>
            <h3 style={{ fontSize: '1.18rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.45rem' }}>{branch.title}</h3>
            <p style={{ fontSize: '0.9rem', lineHeight: 1.6, color: 'var(--text-secondary)' }}>{branch.summary}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {branch.items.map((item) => <LogoTile key={item.name} name={item.name} logo={item.logo} description={item.description} />)}
        </div>
      </SpotlightCard>
    </Reveal>
  );
};

export default function TechStack() {
  return (
    <main>
      <PageHero
        tone="slate"
        eyebrow="Tech Stack"
        title="Technology Stack"
        subtitle="React, TypeScript, Vite, Astro, Tailwind, Radix UI, Supabase, Cloudflare, PostgreSQL, Redis, WordPress REST API, Pagefind, Gemini, OpenAI embeddings, Pydantic AI, and Kreuzberg across the application stack."
        glows={[
          { color: 'rgba(34,211,238,0.24)', top: '-10%', left: '-6%', size: 560 },
          { color: 'rgba(52,211,153,0.18)', bottom: '-20%', right: '-6%', size: 560 }
        ]}
        ridge
      >
        <div className="mt-12 mb-16" style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <TechCube items={cubeItems} size={210} />
        </div>
        <div className="flex justify-center gap-8 mt-4 flex-wrap">
          <div className="text-center"><CountUp value={String(allTools.length)} style={{ fontSize: '2rem', color: '#67E8F9' }} /><p className="kicker mt-1">Tools in use</p></div>
          <div className="text-center"><CountUp value={String(branchGroups.length)} style={{ fontSize: '2rem', color: '#C4B5FD' }} /><p className="kicker mt-1">Stack branches</p></div>
          <div className="text-center"><CountUp value={String(mainStack.length)} style={{ fontSize: '2rem', color: '#6EE7B7' }} /><p className="kicker mt-1">Core tools</p></div>
        </div>
      </PageHero>

      <Section tone="light" pattern="grid" glows={[{ color: 'rgba(167,139,250,0.3)', top: '-10%', right: '-8%', size: 560 }]} padding="py-20">
        <SectionHeading
          title="Main Tool Stack"
          subtitle="A compact view of the primary stack, with supporting technologies grouped into branches below."
          className="mb-10"
          maxWidth={700}
        />

        <Reveal className="mb-10">
          <ChartCard title="Stack composition" caption="Number of tools in each branch of the stack." data={branchGroups.map((b) => ({ name: b.title, value: b.items.length }))} valueLabel="Tools">
            <BarsH scheme="light" data={branchGroups.map((b) => ({ name: b.title, value: b.items.length }))} />
          </ChartCard>
        </Reveal>

        <motion.div {...fadeInUp} className="mb-14">
          <Marquee speed={45}>
            {mainStack.map((tool) => <LogoTile key={tool} name={tool} logo={logoFor(tool)} size="lg" />)}
          </Marquee>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {branchGroups.map((branch, i) => <BranchCard key={branch.title} branch={branch} index={i} />)}
        </div>
      </Section>

      <Section
        tone="plum"
        pattern="dots"
        aurora
        topo
        glows={[
          { color: 'rgba(167,139,250,0.3)', top: '-10%', left: '-6%', size: 600 },
          { color: 'rgba(251,113,133,0.18)', bottom: '-20%', right: '-6%', size: 560 }
        ]}
        padding="py-20"
      >
        <SectionHeading title="Technology Overview" className="mb-14" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {overview.map((cat, i) => {
            const Icon = cat.icon;
            return (
              <Reveal key={cat.title} delay={i * 0.06} className="h-full">
                <SpotlightCard accent={cat.accent} className="h-full" style={{ '--card-pad': '2.25rem' }}>
                  <div className="icon-well mb-5"><Icon className="w-6 h-6" /></div>
                  <h3 style={{ fontSize: '1.15rem', fontWeight: 700, marginBottom: '0.6rem', color: 'var(--text-primary)' }}>{cat.title}</h3>
                  <p style={{ fontSize: '0.92rem', lineHeight: 1.65, color: 'var(--text-secondary)' }}>{cat.desc}</p>
                </SpotlightCard>
              </Reveal>
            );
          })}
        </div>
      </Section>

      <Section
        tone="teal"
        rule={false}
        glows={[
          { color: 'rgba(52,211,153,0.26)', top: '-30%', left: '20%', size: 640 },
          { color: 'rgba(34,211,238,0.22)', bottom: '-30%', right: '10%', size: 560 }
        ]}
        contentWidth={700}
        padding="py-28"
      >
        <ParticlesBg />
        <motion.div {...fadeInUp} className="text-center relative z-10">
          <h2 className="h-section mb-5" style={{ fontSize: 'clamp(1.6rem, 3.5vw, 2.4rem)' }}>Want to Work With Our Stack?</h2>
          <p className="lede mx-auto mb-10">Whether you're a developer or a client who wants to leverage these tools — let's talk.</p>
          <Link to="/contact" className="btn-primary btn-accent" style={{ padding: '1rem 2.5rem', fontSize: '0.85rem' }}>
            Get in Touch <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
        <Ridgeline opacity={0.7} height={140} />
      </Section>
    </main>
  );
}
